import { fetchClient } from "@/api/api";
import type {
	getTradeSettingsOutput,
	updateTradeSettingsInput,
	updateTradeSettingsOutput,
} from "@/api/custom_types";
import useApiError from "@/hooks/useApiError";
import type { ApiError } from "@/utils/fetching";
import type { CustomMutationHook, CustomQueryHook } from "@/utils/react-query";
import { invalidateQueries } from "@/utils/react-query";
import { useMutation, useQuery } from "@tanstack/react-query";
import { TRADE_SETTINGS_SERVICE_KEYS } from "./keys";

export const useGetConfiguration: CustomQueryHook<
	getTradeSettingsOutput,
	undefined,
	getTradeSettingsOutput
> = (options) => {
	return useQuery({
		...options,
		queryKey: TRADE_SETTINGS_SERVICE_KEYS.useGetConfiguration(),
		queryFn: async () => {
			const { data, error } = await fetchClient.GET(
				"/v1/fc/admin/trade-settings/",
				{
					throwOnError: true,
				},
			);
			if (error) throw error;
			return data;
		},
	});
};

export const useUpdateTradeSettings: CustomMutationHook<
	updateTradeSettingsInput,
	updateTradeSettingsOutput
> = () => {
	const { toastApiError } = useApiError();

	return useMutation<
		updateTradeSettingsOutput,
		ApiError,
		updateTradeSettingsInput
	>({
		mutationKey: TRADE_SETTINGS_SERVICE_KEYS.useUpdateTradeSettings(),
		mutationFn: async (body) => {
			const { data, error } = await fetchClient.PATCH(
				"/v1/fc/admin/trade-settings/",
				{ body, throwOnError: true },
			);
			if (error) throw error;
			return data;
		},
		onSuccess: () => {
			invalidateQueries(TRADE_SETTINGS_SERVICE_KEYS.useGetConfiguration());
		},
		onError: (error) => {
			toastApiError(error);
		},
	});
};
